import type { Space } from '@cocoonspace/sdk-js/types/space'
import { features } from '@cocoonspace/shared/config/features'
import {
	BatteryCharging,
	Bike,
	Cigarette,
	Clock10,
	CookingPot,
	Croissant,
	Microwave,
	Monitor,
	PawPrint,
	Phone,
	Presentation,
	Printer,
	Refrigerator,
	ShowerHead,
	Utensils,
	createLucideIcon,
} from 'lucide-react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { SpaceSectionContent } from '~/domains/spaces/components/space-details/space-section-content'
import { SpaceSectionTitle } from '~/domains/spaces/components/space-details/space-section-title'

const ScannerIcon = createLucideIcon('Scanner', [
	[
		'rect',
		{ x: '2', y: '13.3485', width: '19.3333', height: '6.15152', rx: '2' },
	],
	['path', { d: 'M3.53784 5L20 13.5', strokeLinecap: 'round' }],
	[
		'circle',
		{
			cx: '5.07581',
			cy: '16.4243',
			r: '0.878788',
			fill: 'black',
			stroke: 'none',
		},
	],
	[
		'circle',
		{
			cx: '7.71204',
			cy: '16.4243',
			r: '0.878788',
			fill: 'black',
			stroke: 'none',
		},
	],
])

const ErgonomicSeating = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 490.67 490.67'
		width={89 / 1.5}
		height={71 / 1.5}
		strokeWidth={0.4}
		stroke='black'
	>
		<rect
			x='122.667'
			y='475.336'
			width='46'
			height='15.334'
		/>
		<rect
			x='322.265'
			y='475.336'
			width='46.001'
			height='15.334'
		/>
		<rect
			x='222.335'
			y='475.336'
			width='46'
			height='15.334'
		/>
		<path
			d='M368.003,253.001h30.666c12.683,0,23.001-10.318,23.001-23c0-12.683-10.318-23.001-23.001-23.001h-30.666
				c-12.683,0-23.001,10.318-23.001,23.001v61.333h-15.333V42.677C329.669,19.145,310.523,0,286.991,0h-83.313
				c-23.532,0-42.677,19.145-42.677,42.677v248.657h-15.334v-61.333c0-12.683-10.318-23.001-23-23.001H92
				c-12.682,0-23,10.318-23,23.001c0,12.682,10.318,23,23,23h30.667c2.688,0,5.267-0.468,7.667-1.319v39.652h-3.833
				c-19.024,0-34.5,15.478-34.5,34.501s15.476,34.5,34.5,34.5h81.272c3.057,14.998,14.897,26.839,29.896,29.896v39.104h-99.923
				v38.334h15.333v-23h84.59v23h15.333v-23h84.334v23h15.334v-38.334h-99.667v-39.104c14.998-3.057,26.839-14.897,29.895-29.896
				h81.272c19.023,0,34.501-15.477,34.501-34.5s-15.478-34.501-34.501-34.501h-3.833v-39.652
				C362.736,252.534,365.315,253.001,368.003,253.001z M176.334,42.677c0-15.078,12.267-27.344,27.344-27.344h83.313
				c15.077,0,27.344,12.267,27.344,27.344v248.657H176.334V42.677z M122.667,237.667H92c-4.228,0-7.667-3.438-7.667-7.666
				c0-4.228,3.439-7.667,7.667-7.667h30.667c4.228,0,7.667,3.439,7.667,7.667C130.334,234.229,126.895,237.667,122.667,237.667z
				 M245.335,375.668c-9.995,0-18.517-6.41-21.682-15.333h43.363C263.852,369.259,255.33,375.668,245.335,375.668z M364.169,306.669
				c10.569-0.001,19.167,8.597,19.167,19.167c0,10.568-8.598,19.166-19.167,19.166H126.501c-10.568,0-19.167-8.598-19.167-19.166
				c0-10.569,8.599-19.167,19.167-19.167h34.501h168.667H364.169z M360.336,230.002c0-4.228,3.439-7.667,7.667-7.667h30.666
				c4.228,0,7.667,3.439,7.667,7.667c0,4.228-3.439,7.666-7.667,7.666h-30.666C363.776,237.668,360.336,234.23,360.336,230.002z'
		/>
		<rect
			x='199.334'
			y='68.999'
			width='92.001'
			height='15.334'
		/>
		<rect
			x='199.334'
			y='176.335'
			width='92.001'
			height='15.334'
		/>
	</svg>
)

const Terrace = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 463.057 463.057'
		width={89 / 1.5}
		height={71 / 1.5}
	>
		<path
			d='M343.029,311.528v-16.5h8.5c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5h-13.594l3-9h2.594
				c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5h-7.846c-0.005,0-0.01,0-0.015,0h-48.263c-0.012,0-0.024,0-0.036,0h-7.84
				c-4.142,0-7.5,3.358-7.5,7.5c0,4.142,3.358,7.5,7.5,7.5h2.594l3,9h-46.094v-169h212.708c5.5,0,10.067-3.771,11.107-9.172
				c1.04-5.401-1.8-10.599-6.906-12.642L234.382,0.591c-0.245-0.101-0.494-0.187-0.747-0.261c-0.002-0.001-0.005-0.001-0.007-0.002
				c-0.226-0.065-0.454-0.121-0.685-0.165c-0.058-0.011-0.117-0.016-0.176-0.025c-0.175-0.029-0.351-0.059-0.527-0.076
				c-0.223-0.021-0.446-0.031-0.67-0.032c-0.014,0-0.028-0.002-0.041-0.002c-0.238,0-0.475,0.011-0.712,0.034
				c-0.155,0.015-0.309,0.042-0.463,0.066c-0.08,0.013-0.161,0.02-0.24,0.035c-0.22,0.042-0.438,0.096-0.654,0.158
				c-0.013,0.003-0.025,0.005-0.038,0.009c-0.252,0.074-0.501,0.161-0.745,0.261L7.119,89.213
				c-5.106,2.043-7.946,7.241-6.906,12.642c1.04,5.401,5.607,9.172,11.107,9.172h212.708v169h-46.094l3-9h2.594
				c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5h-7.846c-0.005,0-0.01,0-0.015,0h-48.263c-0.012,0-0.024,0-0.036,0h-7.84
				c-4.142,0-7.5,3.358-7.5,7.5c0,4.142,3.358,7.5,7.5,7.5h2.594l3,9h-13.594c-4.142,0-7.5,3.358-7.5,7.5c0,4.142,3.358,7.5,7.5,7.5
				h8.5v16.5c0,8.547,6.953,15.5,15.5,15.5h88.5v82.234c-9.29,3.138-16,11.93-16,22.266v16.5h-16.5c-4.142,0-7.5,3.358-7.5,7.5
				c0,4.142,3.358,7.5,7.5,7.5h80c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5h-16.5v-16.5c0-10.335-6.71-19.127-16-22.266
				v-82.234h88.501v0.001C336.076,327.028,343.029,320.075,343.029,311.528z M325.123,271.028l-3,9h-21.188l-3-9H325.123z
				 M432.585,96.028H330.636L262.67,28.062L432.585,96.028z M30.473,96.028l169.916-67.966l-67.966,67.966H30.473z M153.636,96.028
				l77.893-77.894l77.894,77.894H153.636z M165.123,271.028l-3,9h-21.188l-3-9H165.123z M240.029,431.528v16.5h-17v-16.5
				c0-4.687,3.813-8.5,8.5-8.5C236.216,423.028,240.029,426.841,240.029,431.528z M327.528,312.028h-192
				c-0.275,0-0.499-0.225-0.499-0.5v-16.5h0.474c0.008,0,0.017,0.002,0.025,0.002s0.016-0.002,0.024-0.002h191.953
				c0.008,0,0.016,0.002,0.024,0.002s0.016-0.002,0.025-0.002h0.474v16.5C328.028,311.803,327.804,312.028,327.528,312.028z'
		/>
		<path
			d='M103.529,344.028H30.15l-15.179-121.43c-0.514-4.11-4.268-7.025-8.372-6.512c-4.11,0.514-7.026,4.263-6.512,8.373
				l15.942,127.537v31.533v72c0,4.142,3.358,7.5,7.5,7.5c4.142,0,7.5-3.358,7.5-7.5v-32.5h65v32.5c0,4.142,3.358,7.5,7.5,7.5
				c4.142,0,7.5-3.358,7.5-7.5v-65.734c9.29-3.138,16-11.93,16-22.266C127.029,354.57,116.487,344.028,103.529,344.028z
				 M96.029,408.028h-65v-17h65V408.028z M103.529,376.028h-72.5v-17h72.5c4.687,0,8.5,3.813,8.5,8.5
				S108.216,376.028,103.529,376.028z'
		/>
		<path
			d='M456.459,216.085c-4.111-0.516-7.858,2.402-8.372,6.512l-15.179,121.43h-73.379c-12.958,0-23.5,10.542-23.5,23.5
				c0,10.335,6.71,19.127,16,22.266v65.734c0,4.142,3.358,7.5,7.5,7.5c4.142,0,7.5-3.358,7.5-7.5v-32.5h65v32.5
				c0,4.142,3.358,7.5,7.5,7.5c4.142,0,7.5-3.358,7.5-7.5v-72v-31.533l15.942-127.537C463.485,220.348,460.57,216.6,456.459,216.085
				z M432.029,408.028h-65v-17h65V408.028z M432.029,376.028h-72.5c-4.687,0-8.5-3.813-8.5-8.5s3.813-8.5,8.5-8.5h72.5V376.028z'
		/>
	</svg>
)

const DESK_FEATURES_ICONS: Record<string, React.FC<any>> = {
	// disabled_access: () => <></>,
	external_screen: Monitor,
	printer: Printer,
	scanner: ScannerIcon,
	phone_box: Phone,
	meeting_room: Presentation,
	// standing_desk: () => <></>,
	ergonomic_seating: ErgonomicSeating,
	snacks: Croissant,
	kitchen: CookingPot,
	refrigerator: Refrigerator,
	microwave_oven: Microwave,
	shower: ShowerHead,
	bike_parking: Bike,
	bike_battery_charging: BatteryCharging,
	smoking_room: Cigarette,
	terrace: Terrace,
	restaurant: Utensils,
	late_closing: Clock10,
	pets_welcomed: PawPrint,
} as const

export const SpaceFeaturesList = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const featureList = [
		...(space.features || []),
		...(space.desk_services || []),
	].filter(
		(feature) =>
			features.includes(feature as any) ||
			!!DESK_FEATURES_ICONS[feature as any],
	)

	return (
		<SpaceSectionContent id='services'>
			<SpaceSectionTitle>
				{t(`space:sections.features.title.long`)}
			</SpaceSectionTitle>

			<p>{t(`space:sections.features.body`)}</p>

			<div
				data-testid='features-list-container'
				className='flex flex-row flex-wrap gap-x-4 lg:grid lg:grid-cols-5'
			>
				{featureList.map((feature) => {
					const Icon = DESK_FEATURES_ICONS[feature as any]

					return (
						<div
							key={feature}
							className='flex flex-col items-center'
						>
							<div className='flex h-[71px] w-[89px] items-center justify-center'>
								{features.includes(feature as any) && (
									<Image
										src={`/images/v2/features/${feature}-off.svg`}
										alt={feature}
										width='89'
										height='71'
									/>
								)}

								{!features.includes(feature as any) && !!Icon && (
									<Icon
										strokeWidth={0.8}
										className='size-10'
									/>
								)}
							</div>

							<p className='text-center text-sm'>{t(`features:${feature}`)}</p>
						</div>
					)
				})}
			</div>
		</SpaceSectionContent>
	)
}
